import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ProtectedShell } from "@/features/auth/ProtectedShell";
import { Panel } from "@/components/ui/Panel";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/features/toast/ToastProvider";
import { usePrompt } from "@/features/confirm/ConfirmProvider";
import { formatDate, formatMoney, formatPercent } from "@bookboost/shared/format";

interface BreakdownResponse {
  commission: {
    id: string;
    deal_amount: number | null;
    commission_total: number;
    cash_portion: number;
    equity_portion: number;
    shares_equivalent: number | null;
    currency: string;
    status: "pending" | "earned" | "approved" | "paid";
    earned_at: string | null;
    approved_at: string | null;
    paid_at: string | null;
    reversal_of_id: string | null;
    superseded_at: string | null;
    notes: string | null;
    deals: { deal_name: string | null; hubspot_deal_id: string; amount: number | null; currency: string | null } | null;
    invoices: { stripe_invoice_id: string } | null;
  };
  structure: {
    structure_type: "flat" | "split_equity";
    base_commission_rate: number;
    equity_split_rate: number | null;
    cash_split_rate: number | null;
    equity_annual_cap: number | null;
    cap_currency: string | null;
    effective_from: string | null;
  } | null;
  capStatus: { equity_accrued: number | null; currency: string | null; cap_reached_at: string | null } | null;
  flags: Array<{
    id: string;
    reason: string;
    status: "open" | "resolved";
    resolution_note: string | null;
    resolved_at: string | null;
    created_at: string;
  }>;
}

export const Route = createFileRoute("/dashboard_/commissions/$id")({
  component: () => (
    <ProtectedShell>
      <BreakdownContent />
    </ProtectedShell>
  ),
});

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="muted">{label}</dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </>
  );
}

function BreakdownContent() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const promptInput = usePrompt();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", "commission", id],
    queryFn: () => api.get<BreakdownResponse>(`/api/dashboard/me/commissions/${id}`),
  });

  const flag = useMutation({
    mutationFn: (reason: string) => api.post(`/api/dashboard/me/commissions/${id}/flag`, { reason }),
    onSuccess: () => {
      toast.ok("Flagged for review. Finance will take a look.");
      qc.invalidateQueries({ queryKey: ["dashboard", "commission", id] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't flag."),
  });

  async function onFlag() {
    const reason = await promptInput({
      title: "Flag this commission",
      prompt: "Tell finance what looks wrong. They'll review and get back to you.",
      label: "What's the issue?",
      placeholder: "e.g. the deal amount looks too low",
      multiline: true,
      confirmLabel: "Submit flag",
    });
    if (reason) flag.mutate(reason);
  }

  if (isLoading) return <p className="lede">Loading breakdown…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Commission not found</h2>
        <p>{error instanceof Error ? error.message : "It may not be yours, or may have been removed."}</p>
        <p><Link to="/dashboard">← Back to my commissions</Link></p>
      </Panel>
    );
  }

  const c = data.commission;
  const ccy = c.currency;
  const rate = c.deal_amount && c.deal_amount > 0 ? c.commission_total / c.deal_amount : null;
  const openFlag = data.flags.find((f) => f.status === "open");
  const dl = { display: "grid", gridTemplateColumns: "max-content 1fr", gap: "8px 18px", margin: 0 } as const;

  return (
    <>
      <p style={{ marginBottom: 12 }}>
        <Link to="/dashboard">← Back to my commissions</Link>
      </p>
      <h1>
        {c.deals?.deal_name ?? "Commission"} · <StatusBadge status={c.status} />
      </h1>

      <Panel title="How this was calculated">
        <dl style={dl}>
          <Row label="Deal value (basis)" value={formatMoney(c.deal_amount, ccy)} />
          <Row label="Rate applied" value={rate != null ? formatPercent(rate) : "—"} />
          <Row label="Commission total" value={<strong>{formatMoney(c.commission_total, ccy)}</strong>} />
          <Row label="Cash portion" value={formatMoney(c.cash_portion, ccy)} />
          <Row label="Equity portion" value={formatMoney(c.equity_portion, ccy)} />
          {c.shares_equivalent != null && <Row label="≈ shares" value={c.shares_equivalent.toLocaleString()} />}
        </dl>
        {data.structure?.structure_type === "split_equity" &&
          data.capStatus?.cap_reached_at != null &&
          (c.earned_at == null || data.capStatus.cap_reached_at <= c.earned_at) && (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
            Some equity rolled into cash because your annual equity cap was reached.
          </p>
        )}
      </Panel>

      {data.structure && (
        <Panel title="Structure applied">
          <dl style={dl}>
            <Row label="Type" value={data.structure.structure_type} />
            <Row label="Base rate" value={formatPercent(data.structure.base_commission_rate)} />
            {data.structure.structure_type === "split_equity" && (
              <>
                {data.structure.equity_split_rate != null && (
                  <Row label="Equity split" value={`${(data.structure.equity_split_rate * 100).toFixed(0)}%`} />
                )}
                {data.structure.equity_annual_cap != null && (
                  <Row
                    label="Annual equity cap"
                    value={formatMoney(data.structure.equity_annual_cap, data.structure.cap_currency ?? "EUR")}
                  />
                )}
                {data.capStatus && (
                  <Row
                    label="Equity accrued this year"
                    value={
                      <>
                        {formatMoney(data.capStatus.equity_accrued, data.capStatus.currency ?? data.structure.cap_currency)}
                        {data.capStatus.cap_reached_at && <span className="muted"> · cap reached {formatDate(data.capStatus.cap_reached_at)}</span>}
                      </>
                    }
                  />
                )}
              </>
            )}
            <Row label="In force from" value={formatDate(data.structure.effective_from)} />
          </dl>
        </Panel>
      )}

      <Panel title="Status & source">
        <dl style={dl}>
          <Row label="Earned" value={formatDate(c.earned_at)} />
          <Row label="Approved" value={formatDate(c.approved_at)} />
          <Row label="Paid" value={formatDate(c.paid_at)} />
          {c.invoices?.stripe_invoice_id && <Row label="Triggering invoice" value={<code>{c.invoices.stripe_invoice_id}</code>} />}
          {c.deals?.hubspot_deal_id && <Row label="HubSpot deal" value={<code>{c.deals.hubspot_deal_id}</code>} />}
          {c.notes && <Row label="Notes" value={c.notes} />}
        </dl>
      </Panel>

      <Panel title="Something look wrong?">
        {openFlag ? (
          <div>
            <p style={{ margin: "0 0 6px" }}>
              <span className="badge pending">under review</span>
            </p>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              You flagged this on {formatDate(openFlag.created_at)}: “{openFlag.reason}”. Finance will follow up.
            </p>
          </div>
        ) : (
          <>
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              If a number looks off, flag it and finance will review. Commissions are never edited in place — corrections are made by a reversal + a fresh row.
            </p>
            <button type="button" disabled={flag.isPending} onClick={onFlag}>
              {flag.isPending ? "Submitting…" : "Flag this commission"}
            </button>
          </>
        )}
        {data.flags.filter((f) => f.status === "resolved").length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Resolved flags</p>
            {data.flags
              .filter((f) => f.status === "resolved")
              .map((f) => (
                <p key={f.id} className="muted" style={{ fontSize: 12.5, margin: "4px 0" }}>
                  {formatDate(f.created_at)}: “{f.reason}” → {f.resolution_note ?? "resolved"} ({formatDate(f.resolved_at)})
                </p>
              ))}
          </div>
        )}
      </Panel>
    </>
  );
}
